import { useCallback, useState } from "react";
import { PiCheckCircle, PiExport } from "react-icons/pi";
import { ActionIcon, Button, Text, Tooltip } from "rizzui";
import { toast } from "sonner";
import { Loader } from "../../../components/loader/SpinnerLogo";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import CustomTable from "../../../components/table/CustomTable";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import useFetch from "../../../hooks/useFetch";
import Page from '../../../layout/Page/Page';
import { ExpirationService } from "../../../services/expiration/expirationService";
import { ExpirationApiResponse } from "../../../type/expiration-type";
import ConfirmRemittancesModal from "../components/ConfirmRemittancesModal";
import ImportSepaReturnsModal from "../components/ImportSepaReturnsModal";
import RemittanceModal from "../components/RemittanceModal";
import ExpirationFilters from "./RemittancesFilters";

interface ListRemittancesProps { }

const columnOrder = [
    'paymentIssueIdentifier',
    'corporation',
    'amountRemitted',
    'bankName',
    'quantityRemitted',
    'isConfirmed',
    'createdAt',
    'document',
];

const ListRemittances: React.FC<ListRemittancesProps> = ({ }) => {

    // HOOKS

    const service = new ExpirationService();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { userCan } = usePrivilege();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoadingDocument, setIsLoadingDocument] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isImportReturnsOpen, setIsImportReturnsOpen] = useState<boolean>(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
    const [remittanceIdsToConfirm, setRemittanceIdsToConfirm] = useState<string[]>([]);
    const [isConfirmingRemittances, setIsConfirmingRemittances] = useState<boolean>(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listRemittances(filters);
        return response.getResponseData() as ExpirationApiResponse;
    }, [filters]));

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = { id: row.id };
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR DOCUMENTO A XML
     * @EN EXPORT DOCUMENT TO XML
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleExport = async (doc: any) => {
        setIsLoadingDocument(true);
        try {
            const response = (await service.downloadDocuments(doc.id));
            if (response) {
                const xmlContent = response.getResponseData();
                const blob = new Blob([xmlContent], { type: "application/xml" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${doc.originalName}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                toast.error('Error al exportar el documento');
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoadingDocument(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ABRE EL MODAL DE CONFIRMACIÓN DE REMESAS
     * @EN OPEN REMITTANCE CONFIRMATION MODAL
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleOpenConfirmModal = (sepaDocumentIds: string[]) => {
        const pendingRemittances = sepaDocumentIds.filter((sepaDocumentId) => {
            const remittance = data?.data?.find((item: any) => item.id === sepaDocumentId);
            return remittance && !remittance.isConfirmed;
        });

        if (pendingRemittances.length === 0) {
            toast.info('Las remesas seleccionadas ya están confirmadas');
            return;
        }

        setRemittanceIdsToConfirm(pendingRemittances);
        setIsConfirmModalOpen(true);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CONFIRMAR REMESAS SELECCIONADAS
     * @EN CONFIRM SELECTED REMITTANCES
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleConfirmRemittances = async () => {
        if (remittanceIdsToConfirm.length === 0) return;

        setIsConfirmingRemittances(true);

        try {
            const response = await service.confirmRemittances(remittanceIdsToConfirm);
            const responseData = response.getResponseData();

            if (!responseData?.success) {
                toast.error(responseData?.message || 'No se pudieron confirmar las remesas');
                return;
            }

            const confirmedCount = responseData?.data?.confirmedCount ?? 0;

            if (confirmedCount > 0) {
                toast.success(
                    confirmedCount === 1
                        ? 'Remesa confirmada correctamente'
                        : `Se han confirmado ${confirmedCount} remesas correctamente`
                );
            } else {
                toast.info(responseData?.message || 'No hay remesas pendientes de confirmar');
            }

            setIsConfirmModalOpen(false);
            setRemittanceIdsToConfirm([]);
            refetch();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Error al confirmar remesas');
        } finally {
            setIsConfirmingRemittances(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CIERRA EL MODAL DE CONFIRMACIÓN
     * @EN CLOSE CONFIRMATION MODAL
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleCloseConfirmModal = () => {
        if (isConfirmingRemittances) return;
        setIsConfirmModalOpen(false);
        setRemittanceIdsToConfirm([]);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    largeTable
                    id={'remittances-table'}
                    columnOrder={columnOrder}
                    columnsNotShown={['id', 'document']}
                    data={orderedData()}
                    isLoading={loading}
                    overrideColumns={[
                        {
                            key: 'corporation',
                            render: (row: any) => {
                                return (<Text>{row.row.original.corporation?.name}</Text>)
                            }
                        },
                        {
                            key: 'isConfirmed',
                            render: (row: any) => {
                                return (
                                    <Text className={row.row.original.isConfirmed ? 'font-medium text-green-600' : 'text-gray-500'}>
                                        {row.row.original.isConfirmed ? 'Sí' : 'No'}
                                    </Text>
                                );
                            }
                        }
                    ]}
                    actions={[
                        {
                            label: 'Export',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Exportar documento'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleExport(row.document)}
                                            isLoading={isLoadingDocument}
                                            disabled={isLoadingDocument}
                                        >
                                            <PiExport className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Confirm',
                            permissions: { group: 'expirations', permission: 'generate_sepa' },
                            render: (row: any) => {
                                if (row.isConfirmed) return null;

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Confirmar remesa'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleOpenConfirmModal([row.id])}
                                        >
                                            <PiCheckCircle className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    multiActions={[
                        {
                            label: 'Confirmar',
                            permissions: { group: 'expirations', permission: 'generate_sepa' },
                            render: (selectedRows: any) => {
                                return (
                                    <Button
                                        variant={'outline'}
                                        color={'primary'}
                                        size={'sm'}
                                        onClick={() => handleOpenConfirmModal(selectedRows)}
                                        className='p-3 text-nowrap'
                                    >
                                        Confirmar
                                    </Button>
                                )
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ExpirationApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {userCan('generate_sepa', 'expirations') && (
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => { setIsImportReturnsOpen(true); }}
                                size="sm"
                            >
                                Importar devoluciones
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => { setIsOpen(true); }}
                                size="sm"
                            >
                                Generar SEPA
                            </Button>
                        </div>
                    )}
                </CustomTable>
            )}

            {!data?.data && loading && (<Loader height='40vh' />)}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de remesas'}>
                <ExpirationFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            <RemittanceModal isOpen={isOpen} setIsOpen={setIsOpen} refetch={refetch} />
            <ImportSepaReturnsModal
                isOpen={isImportReturnsOpen}
                setIsOpen={setIsImportReturnsOpen}
                onImported={refetch}
            />
            <ConfirmRemittancesModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmRemittances}
                selectedCount={remittanceIdsToConfirm.length}
                isLoading={isConfirmingRemittances}
            />
        </Page>
    );
};

export default ListRemittances;
